import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Building2, Plus, Edit2, Trash2, Save, X, MapPin, Upload, Loader2, Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import FilterBar from './FilterBar';
import PlacesAutocomplete from '../PlacesAutocomplete';
import GoogleMapPicker from '../GoogleMapPicker';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Suppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newSupplier, setNewSupplier] = useState({ name: '', address: '', latitude: '', longitude: '' });
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [search, setSearch] = useState('');
  const [hasLocFilter, setHasLocFilter] = useState('all');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return suppliers.filter(s => {
      if (q && !(`${s.name || ''} ${s.address || ''}`.toLowerCase().includes(q))) return false;
      const hasLoc = !!(s.location && s.location.latitude && s.location.longitude);
      if (hasLocFilter === 'with' && !hasLoc) return false;
      if (hasLocFilter === 'without' && hasLoc) return false;
      return true;
    });
  }, [suppliers, search, hasLocFilter]);

  useEffect(() => { fetchSuppliers(); }, []);

  const fetchSuppliers = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/suppliers`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await r.json();
      setSuppliers(data);
    } finally {
      setLoading(false);
    }
  };

  const grabCurrentLocation = (setter) => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setter(pos.coords.latitude, pos.coords.longitude),
      () => alert('Location denied')
    );
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    const r = await fetch(`${API_URL}/api/suppliers`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...newSupplier,
        latitude: parseFloat(newSupplier.latitude),
        longitude: parseFloat(newSupplier.longitude)
      })
    });
    if (r.ok) {
      setShowAdd(false);
      setNewSupplier({ name: '', address: '', latitude: '', longitude: '' });
      fetchSuppliers();
    } else {
      alert('Failed to add supplier');
    }
  };

  const startEdit = (s) => {
    setEditing(s.id);
    setEditForm({
      name: s.name,
      address: s.address,
      latitude: s.location?.latitude || '',
      longitude: s.location?.longitude || ''
    });
  };

  const saveEdit = async (id) => {
    const token = localStorage.getItem('token');
    const r = await fetch(`${API_URL}/api/suppliers/${id}`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: editForm.name,
        address: editForm.address,
        latitude: parseFloat(editForm.latitude),
        longitude: parseFloat(editForm.longitude)
      })
    });
    if (r.ok) {
      setEditing(null);
      fetchSuppliers();
    } else {
      const d = await r.json();
      alert(d.detail || 'Update failed');
    }
  };

  const deleteSupplier = async (s) => {
    if (!window.confirm(`Delete supplier "${s.name}"? This cannot be undone.`)) return;
    const token = localStorage.getItem('token');
    const r = await fetch(`${API_URL}/api/suppliers/${s.id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (r.ok) fetchSuppliers();
    else alert('Delete failed');
  };

  // ===== Bulk import (CSV or Excel) =====
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Normalise a parsed sheet/CSV row (Object) into the supplier payload.
  const REQUIRED_COL = 'name';
  const COL_ALIASES = {
    name: ['name', 'supplier', 'supplier name', 'shop name'],
    address: ['address', 'addr', 'location'],
    contact: ['contact', 'mobile', 'phone', 'phone no', 'contact number'],
    latitude: ['latitude', 'lat'],
    longitude: ['longitude', 'lng', 'long', 'lon'],
  };
  const pick = (obj, key) => {
    const aliases = COL_ALIASES[key] || [key];
    for (const k of Object.keys(obj || {})) {
      const lower = String(k).trim().toLowerCase();
      if (aliases.includes(lower)) return obj[k];
    }
    return undefined;
  };
  const normaliseRows = (raw) => raw.map(r => ({
    name: String(pick(r, 'name') ?? '').trim(),
    address: String(pick(r, 'address') ?? '').trim(),
    contact: String(pick(r, 'contact') ?? '').trim(),
    latitude: parseFloat(pick(r, 'latitude')) || 0,
    longitude: parseFloat(pick(r, 'longitude')) || 0,
  })).filter(r => r.name);

  const parseFile = async (file) => {
    const ext = file.name.split('.').pop().toLowerCase();
    if (ext === 'csv') {
      const text = await file.text();
      const cleaned = text.charCodeAt(0) === 0xFEFF ? text.slice(1) : text;
      const wb = XLSX.read(cleaned, { type: 'string' });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      return normaliseRows(XLSX.utils.sheet_to_json(sheet, { defval: '' }));
    }
    // .xlsx / .xls / .ods all handled by xlsx
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: 'array' });
    const sheet = wb.Sheets[wb.SheetNames[0]];
    return normaliseRows(XLSX.utils.sheet_to_json(sheet, { defval: '' }));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true); setImportResult(null);
    try {
      const rows = await parseFile(file);
      if (!rows.length) {
        alert(`File looks empty or has no "${REQUIRED_COL.toUpperCase()}" column.\n\nExpected headers (any case): Name, Address, Contact, Latitude, Longitude.`);
        return;
      }

      const CHUNK = 250;
      const totals = { inserted: 0, skipped_existing: 0, skipped_invalid: 0, duplicates_in_payload: 0, total_received: 0 };
      const token = localStorage.getItem('token');
      for (let i = 0; i < rows.length; i += CHUNK) {
        const chunk = rows.slice(i, i + CHUNK);
        const r = await fetch(`${API_URL}/api/suppliers/bulk-import`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ suppliers: chunk }),
        });
        if (!r.ok) { alert(`Import failed at batch ${i / CHUNK + 1} (HTTP ${r.status}).`); break; }
        const d = await r.json();
        for (const k of Object.keys(totals)) totals[k] += d[k] || 0;
      }
      setImportResult(totals);
      fetchSuppliers();
    } catch (err) {
      console.error(err);
      alert(`Could not read this file: ${err.message || err}`);
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Downloadable Excel template — admin fills it in & re-uploads.
  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      ['Name', 'Address', 'Contact', 'Latitude', 'Longitude'],
      ['Sample Mills', '12 Mill Rd, Coimbatore', '9876543210', 11.0572, 77.34],
    ]);
    ws['!cols'] = [{ wch: 28 }, { wch: 36 }, { wch: 14 }, { wch: 12 }, { wch: 12 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Suppliers');
    XLSX.writeFile(wb, 'supplier-template.xlsx');
  };

  if (loading) return <div className="text-center py-8">Loading suppliers...</div>;

  return (
    <div data-testid="suppliers-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Suppliers ({suppliers.length})</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={downloadTemplate}
            className="flex items-center gap-2 px-3 py-2 border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-lg text-sm"
            data-testid="supplier-template-btn"
            title="Download an Excel template with the expected column headers"
          >
            <Download size={14} /> Template
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls,.ods,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            onChange={handleFileUpload}
            className="hidden"
            data-testid="supplier-csv-input"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            className="flex items-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white rounded-lg text-sm"
            data-testid="supplier-bulk-import-btn"
            title="Upload CSV or Excel with columns: Name, Address, Contact, Latitude, Longitude"
          >
            {importing ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {importing ? 'Importing…' : 'Import CSV / Excel'}
          </button>
          <button onClick={() => setShowAdd(!showAdd)} className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-testid="add-supplier-btn">
            <Plus size={18} /><span>Add Supplier</span>
          </button>
        </div>
      </div>

      {importResult && (
        <div className="mb-4 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm" data-testid="supplier-import-result">
          <strong className="text-emerald-800">Import complete.</strong>{' '}
          <span className="text-gray-700">
            Inserted <strong>{importResult.inserted}</strong>
            {' · '}Skipped (already exists) <strong>{importResult.skipped_existing}</strong>
            {' · '}Duplicates in file <strong>{importResult.duplicates_in_payload}</strong>
            {' · '}Invalid (no name) <strong>{importResult.skipped_invalid}</strong>
          </span>
          <button onClick={() => setImportResult(null)} className="ml-3 text-emerald-700 underline text-xs">dismiss</button>
        </div>
      )}

      {showAdd && (
        <div className="bg-white rounded-xl p-6 shadow-sm border mb-6">
          <h3 className="font-semibold mb-4">Add New Supplier</h3>
          <form onSubmit={handleAdd} className="space-y-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Search Google Places <span className="text-gray-400">(auto-fills name, address &amp; location)</span></label>
              <PlacesAutocomplete
                testId="supplier-places-search"
                placeholder="Type a supplier name or address…"
                onPlacePicked={(p) => setNewSupplier({
                  name: p.name || newSupplier.name,
                  address: p.formatted_address,
                  latitude: p.latitude,
                  longitude: p.longitude
                })}
              />
            </div>
            <input data-testid="supplier-name-input" type="text" placeholder="Supplier Name" value={newSupplier.name} onChange={(e) => setNewSupplier({...newSupplier, name: e.target.value})} className="w-full px-3 py-2 border rounded-lg" required />
            <textarea data-testid="supplier-address-input" placeholder="Address" value={newSupplier.address} onChange={(e) => setNewSupplier({...newSupplier, address: e.target.value})} className="w-full px-3 py-2 border rounded-lg" rows={2} required />
            <div>
              <label className="block text-xs text-gray-500 mb-1">Pin location on map (tap or drag — satellite toggle top-right)</label>
              <GoogleMapPicker
                value={newSupplier.latitude && newSupplier.longitude ? { latitude: parseFloat(newSupplier.latitude), longitude: parseFloat(newSupplier.longitude) } : null}
                onChange={(c) => setNewSupplier(p => ({ ...p, latitude: c.latitude, longitude: c.longitude }))}
                height={280}
              />
              <p className="mt-1 text-[11px] text-gray-500">
                Lat: <span className="font-mono">{newSupplier.latitude ? Number(newSupplier.latitude).toFixed(6) : '—'}</span> ·{' '}
                Lng: <span className="font-mono">{newSupplier.longitude ? Number(newSupplier.longitude).toFixed(6) : '—'}</span>
              </p>
            </div>
            <div className="flex space-x-2">
              <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg" data-testid="supplier-submit-btn">Add Supplier</button>
              <button type="button" onClick={() => setShowAdd(false)} className="px-4 py-2 bg-gray-200 rounded-lg">Cancel</button>
            </div>
          </form>
        </div>
      )}

      <FilterBar
        testId="supplier-filters"
        placeholder="Search by supplier name or address…"
        search={search}
        onSearch={setSearch}
        chips={[
          { key: 'loc', label: 'Map pin', value: hasLocFilter, options: [
            { value: 'with', label: 'With location' }, { value: 'without', label: 'Without location' }
          ]}
        ]}
        onChipChange={(_, v) => setHasLocFilter(v)}
        onClear={() => { setSearch(''); setHasLocFilter('all'); }}
        resultCount={filtered.length}
      />

      <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="supplier-list">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Address</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(s => {
                const isEditing = editing === s.id;
                return (
                  <tr key={s.id} className="hover:bg-gray-50" data-testid={`supplier-row-${s.id}`}>
                    <td className="px-4 py-2">
                      {isEditing ? (
                        <input value={editForm.name} onChange={(e) => setEditForm({...editForm, name: e.target.value})} className="px-2 py-1 border rounded w-full" />
                      ) : (
                        <div className="flex items-center space-x-2">
                          <Building2 size={16} className="text-purple-600" />
                          <span className="font-medium">{s.name}</span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-gray-600 max-w-md">
                      {isEditing ? (
                        <textarea value={editForm.address} onChange={(e) => setEditForm({...editForm, address: e.target.value})} className="px-2 py-1 border rounded w-full" rows={2} />
                      ) : <span className="text-xs">{s.address}</span>}
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-600">
                      {isEditing ? (
                        <div className="space-y-2">
                          <GoogleMapPicker
                            value={editForm.latitude && editForm.longitude ? { latitude: parseFloat(editForm.latitude), longitude: parseFloat(editForm.longitude) } : null}
                            onChange={(c) => setEditForm(p => ({ ...p, latitude: c.latitude, longitude: c.longitude }))}
                            height={220}
                          />
                          <p className="text-[10px] text-gray-500 font-mono">
                            {editForm.latitude ? Number(editForm.latitude).toFixed(6) : '—'}, {editForm.longitude ? Number(editForm.longitude).toFixed(6) : '—'}
                          </p>
                        </div>
                      ) : (
                        <div>
                          <a href={`https://maps.google.com/?q=${s.location?.latitude},${s.location?.longitude}`} target="_blank" rel="noreferrer" className="text-blue-600 underline flex items-center space-x-1">
                            <MapPin size={12} />
                            <span>{s.location?.latitude?.toFixed(4)}, {s.location?.longitude?.toFixed(4)}</span>
                          </a>
                          {s.created_by_name && (
                            <p className="text-[10px] text-gray-400 mt-0.5">by {s.created_by_name}</p>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      {isEditing ? (
                        <>
                          <button onClick={() => saveEdit(s.id)} className="text-green-600 hover:text-green-700 p-1 mx-0.5" data-testid={`save-supplier-${s.id}`} title="Save"><Save size={16} /></button>
                          <button onClick={() => setEditing(null)} className="text-gray-600 hover:text-gray-700 p-1 mx-0.5" title="Cancel"><X size={16} /></button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => startEdit(s)} className="text-blue-600 hover:text-blue-700 p-1 mx-0.5" data-testid={`edit-supplier-${s.id}`} title="Edit"><Edit2 size={16} /></button>
                          <button onClick={() => deleteSupplier(s)} className="text-red-600 hover:text-red-700 p-1 mx-0.5" data-testid={`delete-supplier-${s.id}`} title="Delete"><Trash2 size={16} /></button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan="4" className="px-4 py-8 text-center text-gray-500">{suppliers.length === 0 ? 'No suppliers yet' : 'No suppliers match the current filter'}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
